// Desktop auto-update — USER-CONTROLLED (no silent install).
//
// While the app runs (Tauri, main window only) we CHECK the updater feed
// (tauri.conf.json → plugins.updater.endpoints = widgets.weavein.ai/downloads/latest.json)
// on launch, every 30 minutes, and when the window regains focus (at most every
// 10 minutes). A newer signed build is DOWNLOADED in the background, then
// DesktopUpdateBanner offers "Restart now": one click installs the already-
// downloaded update and relaunches. Nothing installs without that click.
//
// Before (TRUS-638) it checked only once at launch, so a running app never heard
// about a release: users had to quit to see the prompt, then restart again to
// install it.
//
// One shared store, not per-component state: the banner and the Settings card
// both read it, and only one poller / download ever runs.
//
// NOTE: registering the Rust updater plugin is not enough — the app must call
// check()/download()/install() itself. This module does that.

import { isSecondaryAppWindow } from './desktopWindow';
import { useCallback, useEffect, useSyncExternalStore } from 'react';

export type UpdateStatus =
  | 'idle'
  | 'checking'
  | 'available' // found, not downloaded (background download failed) — install downloads first
  | 'downloading'
  | 'ready' // downloaded; "Restart now" installs + relaunches
  | 'installing'
  | 'uptodate'
  | 'error';

export const UPDATE_POLL_MS = 30 * 60 * 1000;
export const FOCUS_MIN_INTERVAL_MS = 10 * 60 * 1000;
/** "Later" hides the ready banner for this long (the update stays downloaded). */
export const SNOOZE_MS = 4 * 60 * 60 * 1000;

type TauriUpdate = {
  version: string;
  body?: string;
  download: (cb?: (e: any) => void) => Promise<void>;
  install: () => Promise<void>;
  downloadAndInstall: (cb?: (e: any) => void) => Promise<void>;
};

export interface UpdaterState {
  status: UpdateStatus;
  version: string | null;
  body: string | null;
  error: string | null;
  progress: number; // 0..100
  /** The last check was user-initiated (menu / Settings): show its outcome. */
  manual: boolean;
  /** Banner hidden by "Later" until this time (ms epoch). */
  snoozedUntil: number;
}

const isTauri = (): boolean => typeof window !== 'undefined' && '__TAURI__' in window;

// Write to the Rust log file (tauri-plugin-log). Native console.* is NOT captured
// by the plugin, so use its info()/error() to make the updater diagnosable.
function logInfo(msg: string): void {
  console.info(msg);
  import('@tauri-apps/plugin-log').then((m) => m.info(msg)).catch(() => {});
}
function logErr(msg: string): void {
  console.error(msg);
  import('@tauri-apps/plugin-log').then((m) => m.error(msg)).catch(() => {});
}

const initial: UpdaterState = {
  status: 'idle',
  version: null,
  body: null,
  error: null,
  progress: 0,
  manual: false,
  snoozedUntil: 0,
};

let state: UpdaterState = initial;
let update: TauriUpdate | null = null;
let lastCheckAt = 0;
let inFlight: Promise<void> | null = null;
let pollerStarted = false;
const listeners = new Set<() => void>();

function set(patch: Partial<UpdaterState>): void {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

function progressTracker(onFinished?: () => void) {
  let downloaded = 0;
  let total = 0;
  return (event: any) => {
    if (event?.event === 'Started') {
      total = event.data?.contentLength ?? 0;
    } else if (event?.event === 'Progress') {
      downloaded += event.data?.chunkLength ?? 0;
      if (total > 0) set({ progress: Math.min(100, Math.round((downloaded / total) * 100)) });
    } else if (event?.event === 'Finished') {
      set({ progress: 100 });
      onFinished?.();
    }
  };
}

async function downloadInBackground(found: TauriUpdate): Promise<void> {
  set({ status: 'downloading', progress: 0, error: null });
  try {
    await found.download(progressTracker());
    set({ status: 'ready', progress: 100 });
    logInfo(`[updater] ${found.version} downloaded; ready to install on restart`);
  } catch (e: any) {
    // Still installable: "Install & Restart" downloads it then.
    const msg = e?.message ? String(e.message) : String(e);
    logErr(`[updater] background download failed: ${msg}`);
    set({ status: 'available', error: state.manual ? msg : null });
  }
}

/**
 * Check the feed. Background checks are silent on failure (logged only);
 * a manual check surfaces the result, including "up to date" and errors.
 * No-op while an update is downloading / downloaded / installing.
 */
export async function checkForUpdates(opts: { manual?: boolean } = {}): Promise<void> {
  // The updater belongs to the main window; a pop-out never checks (two windows
  // would download and relaunch over each other).
  if (!isTauri() || isSecondaryAppWindow()) return;
  const manual = !!opts.manual;
  if (state.status === 'downloading' || state.status === 'ready' || state.status === 'installing') {
    // Already have it: a manual check re-shows the banner; a background tick
    // brings it back once a "Later" snooze has run out.
    if (manual) set({ manual: true, snoozedUntil: 0 });
    else if (state.snoozedUntil && Date.now() >= state.snoozedUntil) set({ snoozedUntil: 0 });
    return;
  }
  if (inFlight) {
    if (manual) set({ manual: true });
    return inFlight;
  }
  lastCheckAt = Date.now();
  set({ status: 'checking', error: null, manual });
  logInfo(`[updater] checking for updates (${manual ? 'manual' : 'background'})…`);
  inFlight = (async () => {
    try {
      const { check } = await import('@tauri-apps/plugin-updater');
      const found = (await check()) as (TauriUpdate & { available?: boolean }) | null;
      if (found && (found.available ?? true)) {
        update = found;
        set({ version: found.version, body: found.body ?? null });
        logInfo(`[updater] update available: ${found.version}`);
        await downloadInBackground(found);
      } else {
        set({ status: 'uptodate' });
        logInfo('[updater] up to date');
      }
    } catch (e: any) {
      const msg = e?.message ? String(e.message) : String(e);
      logErr(`[updater] check failed: ${msg}`);
      // A failed background check (offline, feed hiccup) must not nag.
      set(manual ? { status: 'error', error: msg } : { status: 'idle', error: null });
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** Install the update and relaunch — the one restart. */
export async function restartToUpdate(): Promise<void> {
  if (!update) return;
  set({ error: null, snoozedUntil: 0 });
  try {
    if (state.status === 'ready') {
      set({ status: 'installing' });
      await update.install();
    } else {
      set({ status: 'downloading', progress: 0 });
      await update.downloadAndInstall(progressTracker(() => set({ status: 'installing' })));
    }
    const { relaunch } = await import('@tauri-apps/plugin-process');
    await relaunch();
  } catch (e: any) {
    const msg = e?.message ? String(e.message) : String(e);
    logErr(`[updater] install failed: ${msg}`);
    set({ status: 'error', error: msg, manual: true });
  }
}

/** "Later": keep the downloaded update, hide the banner for a while. */
export function snoozeUpdate(): void {
  set({ snoozedUntil: Date.now() + SNOOZE_MS, manual: false });
}

function startPoller(): void {
  if (pollerStarted || !isTauri() || isSecondaryAppWindow()) return;
  pollerStarted = true;
  void checkForUpdates();
  window.setInterval(() => void checkForUpdates(), UPDATE_POLL_MS);
  const onFocus = () => {
    if (Date.now() - lastCheckAt >= FOCUS_MIN_INTERVAL_MS) void checkForUpdates();
  };
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') onFocus();
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useDesktopUpdater() {
  const s = useSyncExternalStore(subscribe, () => state, () => initial);

  useEffect(() => {
    startPoller();
  }, []);

  const check = useCallback(() => checkForUpdates({ manual: true }), []);
  const install = useCallback(() => restartToUpdate(), []);
  const snooze = useCallback(() => snoozeUpdate(), []);

  return {
    ...s,
    snoozed: s.snoozedUntil > Date.now(),
    check,
    install,
    snooze,
    isDesktop: isTauri(),
  };
}

/** Test-only: reset the shared store between tests. */
export function __resetDesktopUpdaterForTests(): void {
  state = initial;
  update = null;
  lastCheckAt = 0;
  inFlight = null;
  pollerStarted = false;
  listeners.clear();
}
